import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Popper from '@mui/material/Popper';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import { alpha, useTheme, useColorScheme } from '@mui/material';

import { mixins } from 'src/theme';

import Logo from 'src/components/logo';
import { Iconify } from 'src/components/iconify';
import { UserAvatar } from 'src/components/avatar';
import { useSettingsContext } from 'src/components/settings';

import { useAuthContext } from 'src/auth/hooks/use-auth-context';

import { HEADER } from './layoutConfig';
import { MenuButton } from './components/menu-button';

function Header() {
  const theme = useTheme();
  const { user } = useAuthContext();
  const { openSettings, onToggleSettings, setState, setField } = useSettingsContext();
  const { mode, setMode, systemMode } = useColorScheme();

  // State for user menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const [popperOpen, setPopperOpen] = useState(false);
  const [popperAnchorEl, setPopperAnchorEl] = useState<null | HTMLElement>(null);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenSubmenuId(null);
    setPopperOpen(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenSubmenuId(null);
    setPopperOpen(false);
  };

  // Generic submenu open/close
  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    if (openSubmenuId === id) return;
    setPopperAnchorEl(event.currentTarget);
    setOpenSubmenuId(id);
    setPopperOpen(true);
  };
  const handleSubmenuClose = () => {
    setOpenSubmenuId(null);
    setPopperOpen(false);
  };

  // Theme change handler
  const handleThemeChange = (themeId: 'dark' | 'light' | 'system') => {
    setMode(themeId);
    setState({ colorScheme: themeId === 'light' ? 'light' : 'dark' });
    handleMenuClose();
  };

  // Help handler
  const handleHelp = () => {
    handleMenuClose();
  };

  // Sign out handler
  const handleSignOut = () => {
    handleMenuClose();
  };

  // Menu items with handlers
  const MENU_ITEMS = [
    {
      id: 'theme',
      name: 'Theme',
      icon: 'material-symbols:desktop-windows-outline',
      children: [
        {
          id: 'light',
          name: 'Light',
          icon: 'material-symbols:light-mode-outline',
          onClick: () => handleThemeChange('light'),
        },
        {
          id: 'dark',
          name: 'Dark',
          icon: 'material-symbols:dark-mode-outline',
          onClick: () => handleThemeChange('dark'),
        },
        {
          id: 'system',
          name: 'System',
          icon: 'mdi:laptop',
          onClick: () => handleThemeChange('system'),
        },
      ],
    },
    {
      id: 'help',
      name: 'Help & Support',
      icon: 'material-symbols:help-outline',
      onClick: handleHelp,
      disabled: true,
    },
  ];

  useEffect(() => {
    if (mode === 'system' && systemMode) {
      setState({ colorScheme: systemMode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, systemMode]);

  return (
    <AppBar
      sx={{
        ...mixins.bgBlur({
          color: theme.palette.background.paper,
        }),
        borderBottom:
          theme.palette.mode === 'light'
            ? `1px solid ${alpha(theme.palette.grey[500], 0.15)}`
            : `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          height: HEADER.H_MOBILE,
          transition: theme.transitions.create(['height'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <MenuButton
          sx={{
            [theme.breakpoints.up('md')]: { display: 'none' },
          }}
          onClick={() => {
            setField('navLayout', 'hamburger');
          }}
        />
        <Logo width={125} height={30} maxWidth="100%" mode={theme.palette.mode} />
        <Stack
          flexGrow={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
        >
          <Tooltip title="Run Settings" placement="bottom" arrow>
            <IconButton onClick={() => onToggleSettings()}>
              {openSettings ? (
                <Iconify icon="mynaui:panel-right-close" width={24} color="#5D6692" />
              ) : (
                <Iconify icon="mynaui:panel-right-open" width={24} color="#5D6692" />
              )}
            </IconButton>
          </Tooltip>
          {/* User Avatar with Menu */}
          <IconButton
            onClick={handleAvatarClick}
            sx={{
              width: '32px',
              height: '32px',
              p: 0.5,
            }}
          >
            <UserAvatar
              label={`${user?.firstName?.[0]}${user?.lastName?.[0]}`}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  transform: 'scale(1.08)',
                },
              }}
            />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            slotProps={{
              paper: { sx: { minWidth: 220, mt: 1 } },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Stack sx={{ px: 2, py: 1 }}>
              <Typography style={{ fontWeight: 600 }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography style={{ fontSize: 13, color: theme.palette.text.secondary }}>
                {user?.userName}
              </Typography>
            </Stack>
            <Divider />
            {MENU_ITEMS.map((item) => {
              const isSubmenu = !!item.children;
              return (
                <Box key={item.id}>
                  <MenuItem
                    onMouseEnter={(e) => {
                      if (isSubmenu) {
                        handleSubmenuOpen(e, item.id);
                      } else {
                        handleSubmenuClose(); // close any open submenu
                      }
                    }}
                    onClick={() => !isSubmenu && item.onClick?.()}
                    aria-haspopup={isSubmenu}
                    disabled={item.disabled}
                  >
                    <Iconify icon={item.icon} width={20} sx={{ mr: 1 }} />
                    <ListItemText>{item.name}</ListItemText>
                    {isSubmenu && (
                      <Iconify icon="material-symbols:chevron-right" width={20} sx={{ ml: 1 }} />
                    )}
                  </MenuItem>

                  {/* Popper Submenu */}
                  {isSubmenu && openSubmenuId === item.id && (
                    <Popper
                      open={popperOpen}
                      anchorEl={popperAnchorEl}
                      placement="left-start"
                      modifiers={[
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 8],
                          },
                        },
                      ]}
                      style={{ zIndex: 1302 }}
                    >
                      <Paper
                        sx={{
                          minWidth: 150,
                          background:
                            theme.palette.mode === 'light'
                              ? 'linear-gradient(-135deg, #e3f0ff 0%, #ffffff 60%, #fff0f3 100%)'
                              : 'linear-gradient(135deg, #23272F 0%, #181A20 100%)',
                          boxShadow: theme.shadows[16],
                          border: `1px solid ${alpha(theme.palette.common.black, 0.1)}`,
                          color: theme.palette.text.primary,
                          p: 0.5,
                        }}
                      >
                        {item.children.map((child) => (
                          <MenuItem
                            key={child.id}
                            selected={child.id === mode}
                            onClick={() => {
                              child.onClick?.();
                              handleSubmenuClose();
                              handleMenuClose();
                            }}
                          >
                            <Iconify icon={child.icon} width={20} sx={{ mr: 1 }} />
                            {child.name}
                          </MenuItem>
                        ))}
                      </Paper>
                    </Popper>
                  )}
                </Box>
              );
            })}
            <Divider />
            <MenuItem onClick={handleSignOut} disabled>
              <Iconify icon="material-symbols:logout" width={20} sx={{ mr: 1, color: 'red' }} />
              <ListItemText>Sign Out</ListItemText>
            </MenuItem>
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
